import { message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CloseCircle, CloseSquare } from 'iconsax-react';
import { useRejectPromotionMutation } from '../../../../store/services/promotionsService';
import Button from '../../../../components/atoms/Button';
import FormField from '../../../../components/FormField/FormField';
import TitleText from '../../../../components/atoms/TitleText';
import InfoText from '../../../../components/atoms/InfoText';

const REASON_MIN = 20;
const REASON_MAX = 500;
const REASON_PATTERN = /^[\p{L}\p{N}\p{P}\p{S}\s]*$/u;

const rejectReasonSchema = (t: (key: string) => string) =>
  z.object({
    reason: z
      .string()
      .trim()
      .min(REASON_MIN, t('home.reasonValidationMin'))
      .max(REASON_MAX, t('home.reasonValidation'))
      .regex(REASON_PATTERN, t('home.reasonValidation')),
  });

type RejectFormValues = z.infer<ReturnType<typeof rejectReasonSchema>>;

interface PromotionRejectModalProps {
  isOpen: boolean;
  onClose: (success?: boolean) => void;
  promotionId: string;
}

export default function PromotionRejectModal({
  isOpen,
  onClose,
  promotionId,
}: PromotionRejectModalProps) {
  const { t } = useTranslation();
  const [rejectPromotion, { isLoading }] = useRejectPromotionMutation();

  const methods = useForm<RejectFormValues>({
    resolver: zodResolver(rejectReasonSchema(t)),
    defaultValues: { reason: '' },
    mode: 'onTouched',
  });

  const onSubmit = async (data: RejectFormValues) => {
    if (!promotionId) return;
    try {
      await rejectPromotion({
        id: promotionId,
        body: {
          message: data.reason.trim(),
          reason: data.reason.trim(),
        },
      }).unwrap();
      message.success(t('home.requestRejectedSuccessfully', 'Request rejected successfully'));
      methods.reset();
      onClose(true);
    } catch (error: any) {
      const errData = error?.data;
      const errorDetail = errData?.errors
        ? (Object.values(errData.errors)[0] as string)
        : null;
      message.error(
        errorDetail ||
          errData?.message ||
          t('home.somethingWentWrong', 'Something went wrong')
      );
    }
  };

  const handleCancel = () => {
    methods.reset();
    onClose(false);
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      closable={false}
      footer={null}
      centered
      destroyOnHidden
      width={600}
      data-test="promo-reject-modal"
    >
      <div
        className="flex flex-col items-center justify-center gap-4 py-2"
        data-test="promo-reject-modal-content"
      >
        {/* Icon */}
        <div
          className="bg-red-50 w-12 h-12 rounded-xl flex justify-center items-center"
          data-test="promo-reject-modal-icon"
        >
          <CloseSquare size={24} color="#DC2626" />
        </div>

        {/* Title and Description */}
        <div className="flex flex-col gap-1 items-center text-center">
          <TitleText
            text={t('home.rejectRequest')}
            className="!font-semibold"
          />
          <InfoText
            text={t('home.rejectRequestDescription')}
            maxLength="FULL"
          />
        </div>

        {/* Reason Textarea */}
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="w-full mt-2"
            data-test="promo-reject-modal-form"
          >
            <FormField
              id="promo-reject-modal-reason"
              name="reason"
              type="textarea"
              label={t('home.reason')}
              placeholder={t('home.placeholder')}
              maxLength={REASON_MAX}
              helperText={
                methods.formState.errors.reason?.message ||
                (methods.formState.errors.reason ? t('home.reasonHint') : undefined)
              }
              showMaxLength
              data-test="promo-reject-modal-reason-textarea"
            />

            {/* Buttons */}
            <div
              className="flex justify-center w-full gap-3 mt-2"
              data-test="promo-reject-modal-actions"
            >
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                data-test="promo-reject-modal-cancel-btn"
              >
                {t('home.cancel')}
              </Button>
              <Button
                type="submit"
                variant="danger"
                icon={<CloseCircle size={18} color="#ffffff" />}
                loading={isLoading}
                className="w-40"
                data-test="promo-reject-modal-submit-btn"
              >
                {t('home.promo-action-reject')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
}
